import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import InputMask from "react-input-mask";
import AddressAutocomplete from "../components/Driver/AddressAutocomplete";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const PublicGroupJoin = () => {
  const { joinCode } = useParams();
  const [group, setGroup] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    rideLocation: "",
    smsConsent: false,
  });
  const [status, setStatus] = useState({
    type: "loading",
    message: "Loading…",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${apiBaseUrl}/api/public/participant-groups/${joinCode}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("This join link is unavailable.");
        return response.json();
      })
      .then((body) => {
        setGroup(body);
        setStatus({ type: "", message: "" });
      })
      .catch((error) => setStatus({ type: "error", message: error.message }));
  }, [joinCode]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const phoneNumberMask =
    form.phone && form.phone.replace(/[^0-9]/g, "").length > 10
      ? "1 (999) 999-9999"
      : "(999) 999-9999";

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/public/participant-groups/${joinCode}/join-requests`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.message || "Could not submit request.");
      setStatus({
        type: "success",
        message: "Your request has been sent to the admin for approval.",
      });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (status.type === "loading") {
    return (
      <div className="p-8 text-center text-gray-500">{status.message}</div>
    );
  }

  if (status.type === "error" && !group) {
    return <div className="p-8 text-center text-red-600">{status.message}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="mx-auto max-w-xl rounded-xl bg-white p-6 shadow-xl">
        <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
          Request to join
        </p>
        <h1 className="mt-2 text-2xl font-black text-gray-900">
          {group?.groupName}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Submit your information and an admin will review your request before
          adding you to this group.
        </p>

        {status.type === "success" ? (
          <div className="mt-5 rounded-lg bg-green-50 p-4 font-semibold text-green-700">
            {status.message}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                className="input input-bordered w-full"
                placeholder="First name"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                required
              />
              <input
                className="input input-bordered w-full"
                placeholder="Last name"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                required
              />
            </div>
            <InputMask
              className="input input-bordered w-full"
              mask={phoneNumberMask}
              placeholder="Phone number"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              required
              autoComplete="tel"
            />
            <input
              className="input input-bordered w-full"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
            <AddressAutocomplete
              value={form.rideLocation}
              onChange={(value) => update("rideLocation", value)}
              suggestionsUrl={`/api/public/participant-groups/${joinCode}/address-suggestions`}
              placeholder="Pickup location"
              disabled={saving}
            />
            <label className="flex gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="checkbox checkbox-sm mt-1"
                checked={form.smsConsent}
                onChange={(e) => update("smsConsent", e.target.checked)}
                required
              />
              <span>
                I agree to receive SMS messages related to this group's events,
                including RSVP reminders and ride coordination messages.
              </span>
            </label>
            {status.message && (
              <p className="text-sm font-semibold text-red-600">
                {status.message}
              </p>
            )}
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-[#004a7e] px-4 py-3 font-bold text-white disabled:opacity-50"
            >
              {saving ? "Submitting…" : "Request to Join"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PublicGroupJoin;
